import { WellframeApi } from 'store/api';
import {
  CodeCheckApiResponse,
  CheckUserEligibilityResponseBody,
  CheckUserEligibilityQueryParams,
  CheckUserEligibilityRequestBody,
  EnrollUserResponseBody,
  RegisterUserResponseBody,
  RegisterUserRequestBody,
  SSOEligibilityCheckRequestBody,
  SSOEligibilityCheckResponseBody,
} from 'features/signUp/types';

export const signupService = WellframeApi.injectEndpoints({
  endpoints: (builder) => ({
    codeCheck: builder.query<CodeCheckApiResponse, string>({
      query: (accessCode) => ({
        url: '/mobileapi/v1/code-check',
        method: 'GET',
        params: { code: accessCode },
      }),
    }),
    getUserEligibility: builder.query<
      CheckUserEligibilityResponseBody,
      CheckUserEligibilityQueryParams
    >({
      query: ({ member_id, code, dob }) => {
        const params = member_id && dob ? { member_id, code, dob } : { code };

        return {
          url: '/mobileapi/v1/members/eligibility-data',
          method: 'GET',
          params,
        };
      },
    }),
    getNonSSOUserEligibility: builder.mutation<
      CheckUserEligibilityResponseBody,
      CheckUserEligibilityRequestBody
    >({
      query: (body) => ({
        url: '/mobileapi/v2/members/eligibility-check',
        method: 'POST',
        body,
      }),
    }),
    /**
     * SSO Eligibility Check - POST endpoint with JWT authentication.
     * This endpoint requires JWT Bearer token in Authorization header (automatically added by prepareApiHeaders).
     * Returns member_id and active_user_token for invited users.
     *
     * @param code - The access code for eligibility verification
     */
    getSSOUserEligibility: builder.mutation<
      SSOEligibilityCheckResponseBody,
      SSOEligibilityCheckRequestBody
    >({
      query: (body) => ({
        url: '/mobileapi/v2/members/sso-eligibility-check',
        method: 'POST',
        body,
      }),
    }),
    /**
     * Registers a user.
     *
     * If the user is invited, a `wellframeUserId` and `activeUserToken` must be provided.
     * These values are provided in the request and indicate that it must be a PATCH instead of a POST,
     * as the user already exists on the Wellframe server.
     *
     * @param wellframeUserId An invited user's Wellframe user ID. Undefined for self-onboarding users.
     * @param activeUserToken An invited user's active user token. Undefined for self-onboarding users.
     * @param requestBody The request body.
     */
    registerUser: builder.mutation<
      RegisterUserResponseBody,
      { wellframeUserId?: string; activeUserToken?: string; requestBody: RegisterUserRequestBody }
    >({
      query: ({ wellframeUserId, activeUserToken, requestBody }) => {
        const isInvitedUser = !!wellframeUserId;
        return {
          url: isInvitedUser ? `/mobileapi/v1/members/${wellframeUserId}` : '/mobileapi/v1/members',
          method: isInvitedUser ? 'PATCH' : 'POST',
          headers: activeUserToken ? { 'User-Token': activeUserToken } : undefined,
          body: requestBody,
        };
      },
    }),
    /**
     * Enrolls an already approved and registered user.
     * @param wellframeUserId The user's Wellframe user ID.
     * @param activeUserToken The user's active user token.
     */
    enrollUser: builder.mutation<
      EnrollUserResponseBody,
      { wellframeUserId: string; activeUserToken?: string }
    >({
      query: ({ wellframeUserId, activeUserToken }) => ({
        url: `/mobileapi/v1/members/${wellframeUserId}`,
        method: 'PATCH',
        headers: activeUserToken ? { 'User-Token': activeUserToken } : undefined,
        body: { program_user: { status: 'enrolled' } },
      }),
    }),
  }),
});

export const {
  useLazyGetUserEligibilityQuery,
  useGetNonSSOUserEligibilityMutation,
  useGetSSOUserEligibilityMutation,
  useCodeCheckQuery,
  useLazyCodeCheckQuery,
  useRegisterUserMutation,
  useEnrollUserMutation,
} = signupService;
